import { Component, OnInit, output, input, inject } from "@angular/core";
import { InputModel } from 'gast-onset-ng2';
import { PlacementTestsService } from '../services/placement-tests.service';
import { NgClass, NgStyle } from "@angular/common";
import { InputComponent } from "../exercise/input/input.component";
import { ClozeTestStyleModel } from "../model/cloze-test-style-model";

const FIRST_KEYBOARD_ROW = ["w", "e", "r", "t", "z", "u", "i", "o", "p", "ü"];

const KEYBOARD_ROWS = [
  ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ö", "ä"],
  ["y", "x", "c", "v", "b", "n", "m", "ß"],
];
/**
 * Exercise example is only for mobile
 * It purpose is to familiarize the mobile user with the UI ( virtual keyboard)
 */
@Component({
  selector: "cloze-test-exercise-example",
  templateUrl: "./exercise-example.component.html",
  standalone: true,
  styleUrls: ["./exercise-example.component.scss"],
  imports: [NgClass, NgStyle, InputComponent],
})
export class ExerciseExampleComponent implements OnInit {
  readonly #placementTestsService = inject(PlacementTestsService);
  readonly FIRST_KEYBOARD_ROW = FIRST_KEYBOARD_ROW;
  readonly KEYBOARD_ROWS = KEYBOARD_ROWS;
  inputs: InputModel[] = [];
  lineHeight = 1.75;
  readyCheckbox = false;
  exampleDone = false;
  title = "Computer";

  readonly mobileMode = input(false);
  readonly clozeTestStyleModel = input({} as ClozeTestStyleModel);
  readonly cTestButtonColor = input("");
  readonly cTestLogo = input("");
  readonly lang = input("de");

  readonly startTest = output<boolean>();

  ngOnInit() {
    this.#initTestExample(this.lang());
    // set inputNumber to first cTest input
    this.#placementTestsService.inputNumber = this.inputs[0].id as number;
  }

  toggleStartTest(): void {
    this.startTest.emit(true);
  }

  toggleExampleDone(): void {
    this.exampleDone = true;
  }

  removeLastCharacter(event: TouchEvent): void {
    this.#placementTestsService.removeLastCharacterFromInput();
    const el = event.srcElement as Element;
    el.classList.add("mobile-keyboard-button-active");
    event.stopPropagation();
    event.preventDefault();
  }

  goToPreviousInput(event: TouchEvent): void {
    if (!this.#placementTestsService.inputNumber) {
      this.#placementTestsService.inputNumber = this.inputs[0].id as number;
    } else if (this.#placementTestsService.inputNumber > 1) {
      const currentInputId = this.#placementTestsService.inputNumber;
      const inputArrayIndex = this.inputs.findIndex((i) => i.id === currentInputId);
      if (this.inputs[inputArrayIndex - 1]) {
        this.#placementTestsService.inputNumber = this.inputs[inputArrayIndex - 1].id as number;
      }
    }
    event.stopPropagation();
    event.preventDefault();
  }

  goToNextInput(event: TouchEvent): void {
    if (!this.#placementTestsService.inputNumber) {
      this.#placementTestsService.inputNumber = this.inputs[0].id as number;
    } else {
      const currentInputId = this.#placementTestsService.inputNumber;
      const inputArrayIndex = this.inputs.findIndex((i) => i.id === currentInputId);
      if (this.inputs[inputArrayIndex + 1]) {
        this.#placementTestsService.inputNumber = this.inputs[inputArrayIndex + 1].id as number;
      }
    }
    event.stopPropagation();
    event.preventDefault();
  }

  assignCharacter(specialCharacter: string, event: TouchEvent): void {
    this.#placementTestsService.setSpecialCharacterToInput(specialCharacter);
    const el = event.srcElement as Element;
    el.classList.add("mobile-keyboard-button-active");
    event.stopPropagation();
    event.preventDefault();
  }

  onTouchend(event: TouchEvent): void {
    const el = event.srcElement as Element;
    el.classList.remove("mobile-keyboard-button-active");
    event.stopPropagation();
    event.preventDefault();
  }

  #initTestExample(lang: string) {
    if (lang === "de") {
      const input1 = {
        id: 1,
        text: "Computer machen das Leben leichter. Schnell",
        lastWord: "h",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 1,
        },
      };

      const input2 = {
        id: 2,
        text: " man ",
        lastWord: "ei",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 2,
        },
      };

      const input3 = {
        id: 3,
        text: " neuen ",
        lastWord: "Te",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 3,
        },
      };

      const input4 = {
        id: 4,
        text: " geschrieben, ",
        lastWord: "bearb",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 4,
        },
      };

      const input5 = {
        id: 5,
        text: " und ",
        lastWord: "ausged",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 5,
        },
      };

      const input6 = {
        id: 6,
        text: ". Mit ",
        lastWord: "ei",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 6,
        },
      };

      const input7 = {
        id: 7,
        text: " E-Mail ",
        lastWord: "ka",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 7,
        },
      };

      const input8 = {
        id: 8,
        text: " man ",
        lastWord: "al",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 8,
        },
      };

      const input9 = {
        id: 9,
        text: " an ",
        lastWord: "Freu",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 9,
        },
      };

      const input10 = {
        id: 10,
        text: " oder ",
        lastWord: "Koll",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 10,
        },
      };

      const input11 = {
        id: 11,
        text: " schicken. Aber was passiert, wenn der Computer einmal streikt? ",
        lastWord: "",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 11,
        },
      };

      this.inputs.push(input1, input2, input3, input4, input5, input6, input7, input8, input9, input10, input11);
    } else if (lang === "en") {
      this.title = "Learning";
      const input1 = {
        id: 1,
        text: "Learning comes naturally to babies: they learn about their environment using their senses. Probably ",
        lastWord: "t",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 1,
        },
      };
      const input2 = {
        id: 2,
        text: " experience",
        lastWord: "a",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 2,
        },
      };
      const input3 = {
        id: 3,
        text: " school ",
        lastWord: "ma",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 3,
        },
      };
      const input4 = {
        id: 4,
        text: " a ",
        lastWord: "l",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 4,
        },
      };
      const input5 = {
        id: 5,
        text: " of ",
        lastWord: "peo",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 5,
        },
      };
      const input6 = {
        id: 6,
        text: " think ",
        lastWord: "th",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 6,
        },
      };
      const input7 = {
        id: 7,
        text: " learning ",
        lastWord: "alw",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 7,
        },
      };
      const input8 = {
        id: 8,
        text: " means ",
        lastWord: "sit",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 8,
        },
      };
      const input9 = {
        id: 9,
        text: " at a ",
        lastWord: "de",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 9,
        },
      };
      const input10 = {
        id: 10,
        text: " listening ",
        lastWord: "t",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 10,
        },
      };
      const input11 = {
        id: 11,
        text: " someone, ",
        lastWord: "tak",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 11,
        },
      };
      const input12 = {
        id: 12,
        text: " notes ",
        lastWord: "a",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 12,
        },
      };
      const input13 = {
        id: 13,
        text: " then ",
        lastWord: "revi",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 13,
        },
      };
      const input14 = {
        id: 14,
        text: " for ",
        lastWord: "ex",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 14,
        },
      };
      const input15 = {
        id: 15,
        text: ". In ",
        lastWord: "fa",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 15,
        },
      };
      const input16 = {
        id: 16,
        text: ", this ",
        lastWord: "i",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 16,
        },
      };
      const input17 = {
        id: 17,
        text: " only ",
        lastWord: "o",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 17,
        },
      };
      const input18 = {
        id: 18,
        text: " of ",
        lastWord: "ma",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 18,
        },
      };
      const input19 = {
        id: 19,
        text: " possible ",
        lastWord: "wa",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 19,
        },
      };
      const input20 = {
        id: 20,
        text: " in ",
        lastWord: "wh",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 20,
        },
      };
      const input21 = {
        id: 21,
        text: " learning takes place and it is definitely not suitable for all topic or subjects.",
        lastWord: "",
        answer: {
          antwort: "",
          aufgabeId: null,
          buchungId: null,
          nr: 21,
        },
      };
      this.inputs.push(
        input1,
        input2,
        input3,
        input4,
        input5,
        input6,
        input7,
        input8,
        input9,
        input10,
        input11,
        input12,
        input13,
        input14,
        input15,
        input16,
        input17,
        input18,
        input19,
        input20,
        input21
      );
    }
  }
}
